<script lang="ts">
  // History view per INTERFACE-SPECIFICATION.md §9.
  //
  // Per-product timeline, version comparison via the read-path
  // Worker's ?hash= query, partial / full rollback flow. Rollback
  // composes a new draft populated with the chosen historical values;
  // commit 5's CubeEditor + commit 6's SubmitForReview then drive the
  // proposal lifecycle.

  import { onMount } from 'svelte';
  import { bootstrapManifest, catalogueState } from '../lib/catalogue-store.svelte';
  import { PRODUCTS, findProduct, productKey } from '../lib/products';
  import type { Jurisdiction, ProductId, CubeCellValue } from '../lib/types';
  import HistoryTimeline from '../components/HistoryTimeline.svelte';
  import HistoryComparison from '../components/HistoryComparison.svelte';
  import {
    beginEditing,
    stagedEditsState,
    stageCell,
    stageAmountEdit,
    stageFeeEdit,
    stageAddPurpose,
    stageRemovePurpose,
    stageUpdatePurpose,
    clearEditing
  } from '../lib/staged-edits.svelte';
  import { navigate, route } from '../lib/router.svelte';
  import type { HistoryRollbackPayload } from '../components/HistoryComparison.svelte';
  import type { FeePath, LoanPurpose } from '../lib/staged-edits';

  const jurisdiction = $derived((route.current.params.jurisdiction as Jurisdiction | undefined) ?? 'au');
  const product = $derived((route.current.params.product as ProductId | undefined) ?? 'unsecured-personal-loan');

  const activeProductKey = $derived(productKey({ jurisdiction, product }));
  let selectedHashes = $state(new Set<string>());

  // Pre-select a pair when navigated with ?compare=hashA,hashB. Used by
  // the Approved & Queued card's "Compare against current" link so the
  // reviewer lands directly on the comparison view rather than ticking
  // two checkboxes manually. The selection only applies once per
  // product key — switching products clears it the same way a manual
  // tick clears, below.
  let lastSeenKey = $state('');
  let lastAppliedCompareKey = $state('');
  $effect(() => {
    if (activeProductKey !== lastSeenKey) {
      lastSeenKey = activeProductKey;
      selectedHashes = new Set();
      lastAppliedCompareKey = '';
    }
    const compareParam = route.current.query.get('compare');
    const compareKey = `${activeProductKey}|${compareParam ?? ''}`;
    if (compareParam && lastAppliedCompareKey !== compareKey) {
      lastAppliedCompareKey = compareKey;
      const hashes = compareParam.split(',').map((h) => h.trim()).filter(Boolean);
      if (hashes.length === 2) {
        selectedHashes = new Set(hashes);
      }
    }
  });

  onMount(async () => {
    if (!catalogueState.manifest) await bootstrapManifest();
  });

  function toggle(hash: string) {
    const next = new Set(selectedHashes);
    if (next.has(hash)) {
      next.delete(hash);
    } else {
      if (next.size >= 2) next.delete([...next][0]!);
      next.add(hash);
    }
    selectedHashes = next;
  }

  const selectedHashList = $derived([...selectedHashes]);
  const orderedPair = $derived(orderHashes());

  function orderHashes(): { left: string; right: string } | null {
    if (selectedHashList.length !== 2) return null;
    // Order by effective-from to make the "left → right" direction natural.
    const versions = catalogueState.manifest?.jurisdictions?.[jurisdiction]?.[product]?.versions ?? [];
    const lookup = new Map(versions.map((v) => [v.git_hash, v.effective_from.utc]));
    const sorted = [...selectedHashList].sort((a, b) => ((lookup.get(a) ?? '') < (lookup.get(b) ?? '') ? -1 : 1));
    return { left: sorted[0]!, right: sorted[1]! };
  }

  function changeProduct(event: Event) {
    const target = event.target as HTMLSelectElement;
    const [j, p] = target.value.split('/');
    if (j && p) navigate(`/history/${j}/${p}`);
  }

  function startRollback(payload: HistoryRollbackPayload) {
    clearEditing();
    beginEditing(jurisdiction, product);

    // Rates: stage each cell as a fresh edit. "before" is the current
    // cube; CubeEditor will read it from the live catalogue on load.
    if (payload.value_type === 'rates') {
      if (payload.cells.length === 0) return;
      for (const cell of payload.cells) {
        stageCell(
          cell.coordinates,
          { interest_rate_pa: null, max_loan_amount: null } as CubeCellValue,
          'interest_rate_pa',
          Number(cell.new_value.interest_rate_pa ?? 0)
        );
        if (cell.new_value.max_loan_amount !== undefined && cell.new_value.max_loan_amount !== null) {
          stageCell(
            cell.coordinates,
            { interest_rate_pa: null, max_loan_amount: null } as CubeCellValue,
            'max_loan_amount',
            Number(cell.new_value.max_loan_amount)
          );
        }
      }
    } else if (payload.value_type === 'amounts') {
      // The rollback payload carries the right-side snapshot's value
      // as `before` so the staged-edits store can render an honest
      // "$before → $new_value" diff in the SubmitForReview preview.
      // The server uses the live catalogue baseline on validate /
      // promote regardless, so this is purely for the SPA preview.
      for (const edit of payload.edits) {
        stageAmountEdit(edit.field, edit.before, edit.new_value);
      }
    } else if (payload.value_type === 'fees') {
      for (const edit of payload.edits) {
        // FeePath is a string-union; the diff helper hands us a
        // 'fees.…' path which matches.
        stageFeeEdit(edit.path as FeePath, edit.before, edit.new_value);
      }
    } else if (payload.value_type === 'purposes') {
      for (const edit of payload.edits) {
        if (edit.action === 'add') {
          stageAddPurpose(edit.purpose as LoanPurpose);
        } else if (edit.action === 'remove') {
          stageRemovePurpose(edit.purpose_id, edit.before as LoanPurpose);
        } else if (edit.action === 'update') {
          stageUpdatePurpose(edit.purpose_id, edit.before as LoanPurpose, edit.purpose as LoanPurpose);
        }
      }
    }
    navigate(`/catalogue/detail/${jurisdiction}/${product}`);
  }
</script>

<section>
  <header>
    <h2>History</h2>
    <label>
      <span class="muted">Product</span>
      <select value={activeProductKey} onchange={changeProduct}>
        {#each PRODUCTS as p (p.jurisdiction + p.product)}
          <option value={productKey(p)}>{p.label} — {p.customerLabel}</option>
        {/each}
      </select>
    </label>
  </header>

  <p class="muted">Tick two versions to see the per-cell delta and reach the rollback flow. Single-tick a version to read the point-in-time snapshot.</p>

  <HistoryTimeline {jurisdiction} {product} selected={selectedHashes} onToggle={toggle} />

  {#if orderedPair}
    <HistoryComparison {jurisdiction} {product} leftHash={orderedPair.left} rightHash={orderedPair.right} onRollback={startRollback} />
  {:else if selectedHashList.length === 1}
    <p class="muted">Point-in-time view: tick a second version to compare, or open the Catalogue Detail view with ?hash= to read this snapshot.</p>
  {/if}
</section>

<style>
  section header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: var(--space-4);
    margin-bottom: var(--space-3);
    flex-wrap: wrap;
  }
</style>
